/**
 * ConversationsV2Page — WhatsApp-Web-style two-pane chat UI for the
 * new unified Conversation subsystem (iter122).
 *
 * Layout:
 *   ┌─────────────┬──────────────────────────────┐
 *   │   Sidebar   │      Active Conversation     │
 *   │  (list)     │  - Title + PCS + Active      │
 *   │             │  - Message timeline          │
 *   │             │  - Composer                  │
 *   └─────────────┴──────────────────────────────┘
 *
 * Design parity with the legacy SMS ConversationsPage: left rail
 * shows my Conversations sorted by most-recent activity with unread
 * pills; right pane shows the selected Conversation with a sticky
 * header, a scrollable message area, and a sticky composer at the
 * bottom.
 *
 * Backend: /api/conv/* (see conversation_routes.py). All mutations
 * are authenticated via X-User-Email (existing auth). The UI does
 * not yet expose Apply-Up (request upstream) — that's a follow-up.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, PaperPlaneTilt, Users, Plus, DotsThreeVertical,
  MagnifyingGlass, ChatCircle, Info, SignOut as SignOutIcon,
  Bell, Check, X as XIcon, ArrowUp, ArrowDown, EnvelopeSimple,
  UserPlus, Paperclip, PencilSimple, Trash, Eye,
  File as FileIcon, CircleNotch, At, Gear,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";
import AuthImage from "@/components/AuthImage";
// iter135.12 — useConvSocket replaced by useGlobalConvSocket
// (single global WS multiplexed across every conversation the
// user is in).
import { toast } from "sonner";
import { useConfirm } from "@/hooks/use-confirm";
import Modal from "@/pages/ConversationsV2/Modal";
import { InitiateModal, AddCompanyModal } from "@/pages/ConversationsV2/CompanyPickers";
import {
  getCached as msgCacheGet,
  setCached as msgCacheSet,
  appendCached as msgCacheAppend,
  patchCached as msgCachePatch,
} from "@/lib/convMessageCache";
// iter136.7 — IndexedDB-backed deep-history cache. Async, ~500
// msgs/conv soft cap, 30-day TTL. Coexists with the synchronous
// localStorage cache above (which still drives instant first-paint).
import * as msgIDB from "@/lib/conv/messageCacheDB";
import {
  ShareButton, ShareBanner, ViewerModal, useShareState,
} from "@/pages/ConversationsV2/SharePanel";
import { convStore } from "@/lib/conv/store";
import {
  useConvList, useMessageOrder, useTypingUsers, useReadsByUser,
} from "@/lib/conv/hooks";
import { convSocket, useGlobalConvSocket } from "@/lib/conv/socket";
import { notificationStore } from "@/lib/conv/notificationStore";
import { SidebarRow, avatarColor } from "@/pages/ConversationsV2/SidebarRow";
import { MessageList } from "@/pages/ConversationsV2/MessageList";
import { MentionPicker } from "@/pages/ConversationsV2/MentionPicker";
import NotifPrefsPanel from "@/pages/ConversationsV2/NotifPrefsPanel";

export default function ConversationsV2Page({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();

  const [me, setMe] = useState(null);
  // iter135.11 — Phase A: ``conversations``, ``messages``, typing,
  // and read-receipts now live in the external ``convStore`` instead
  // of the page's flat ``useState``. The hooks subscribe to one
  // slice each via ``useSyncExternalStore``, so a typing tick (or a
  // message landing in conv X) doesn't re-render the sidebar rows
  // or message bubbles for OTHER conversations. See
  // ``/app/frontend/src/lib/conv/store.js`` for the full design.
  const conversations = useConvList();
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState("");

  const selectedId = searchParams.get("c") || "";
  const [conv, setConv] = useState(null);
  const messageOrder = useMessageOrder(selectedId);
  const messages = useMemo(
    () => messageOrder.map((id) => convStore.getMessage(selectedId, id)).filter(Boolean),
    [messageOrder, selectedId],
  );
  const typingUsers = useTypingUsers(selectedId);
  const readsByUser = useReadsByUser(selectedId);
  const [loadingConv, setLoadingConv] = useState(false);
  const [composerValue, setComposerValue] = useState("");
  const [showInitiate, setShowInitiate] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [showInvite, setShowInvite] = useState(false);
  // iter133 — Closed-conversation sidebar toggle. Default OFF so the
  // sidebar shows only live conversations. Toggling ON refetches with
  // ?include_archived=true and visually marks closed rows.
  const [showClosed, setShowClosed] = useState(false);
  // Header overflow menu (DotsThreeVertical) — controls the
  // "Close conversation" item.
  const [showConvMenu, setShowConvMenu] = useState(false);
  // iter136 — unread @-mention count for the bell-mention badge.
  const [mentionsCount, setMentionsCount] = useState(0);

  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  // iter135.15 — Phase C: MessageList is virtualised (react-virtuoso).
  // The page talks to it via this imperative handle for snap-to-
  // bottom and reads at-bottom state via ``onAtBottomChange``.
  const messageListRef = useRef(null);
  const atBottomRef = useRef(true);
  // Stable callback for Virtuoso's scrollerRef — creating a new
  // function every render would cause Virtuoso's internal scroll
  // effect to re-subscribe with a stale cleanup pointer.
  const bindScroller = useCallback((el) => { messagesScrollRef.current = el; }, []);
  const fileInputRef = useRef(null);
  // iter135.7 — when the user has scrolled up by more than ~120 px
  // from the bottom, surface a floating "jump to latest" arrow.
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  // iter134.3 — scroll-up paginator. ``hasMoreHistory`` flips false
  // once the server returns fewer than ``limit`` rows. ``loadingMore``
  // guards against firing multiple parallel back-fills as the user
  // keeps scrolling.
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Tracks the message_id of the LATEST visible message so the auto-
  // scroll-to-bottom effect only fires for actual incoming messages,
  // not when older history is prepended via the scroll paginator.
  const lastMsgIdRef = useRef(null);
  // Marks the FIRST scroll-to-bottom for a freshly-opened conversation
  // so we can use ``behavior: "auto"`` (instant snap) instead of the
  // smooth animation that's appropriate for incoming messages.
  const firstScrollRef = useRef(true);

  // ── Load "me" once ────────────────────────────────────────────
  useEffect(() => {
    api.get("/conv/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  // iter139.5 — Sync the saved per-user language from the server.
  // External invitees use the same Person record across devices; if
  // they picked French on their laptop and now open the app on their
  // phone, we want the phone to switch to French too. This runs once
  // on mount and silently no-ops when the user has no preference yet.
  useEffect(() => {
    let cancelled = false;
    api.get("/conv/me/lang").then((r) => {
      if (cancelled) return;
      const saved = (r.data?.lang || "").toLowerCase();
      if (saved && saved !== lang && ["en", "fr", "es"].includes(saved)) {
        setLang(saved);
      }
    }).catch(() => { /* network glitch — keep current lang */ });
    return () => { cancelled = true; };
    // We deliberately depend on neither lang nor setLang — this is a
    // one-shot bootstrap that should fire exactly once per page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter137.3 — Track which conv is currently focused so we suppress
  // sound + badge bumps for messages the user is literally watching
  // arrive. (We deliberately do NOT mark-all-read on page mount —
  // messages should only be marked read when the user actually reads
  // them, which happens via the per-conv `read` flow already wired up
  // by `MessageList` as messages scroll into view.)
  useEffect(() => {
    return () => notificationStore.setActiveConversation(null);
  }, []);

  useEffect(() => {
    notificationStore.setActiveConversation(selectedId || null);
  }, [selectedId]);

  // ── Poll inbox badge every 30s ────────────────────────────────
  // iter136.16 — silent: true so transient network blips during a
  // screen-share don't surface noisy toasts. The poll retries on
  // its own 30 s cadence, so a one-off failure is invisible.
  const reloadInboxCount = useCallback(() => {
    api.get("/conv/requests/badge", { silent: true })
      .then((r) => setInboxCount(Number(r.data?.pending_incoming || 0)))
      .catch(() => {});
  }, []);
  // iter136 — Poll the @-mention unread badge on the same cadence.
  const reloadMentionsCount = useCallback(() => {
    api.get("/conv/mentions/unread-count", { silent: true })
      .then((r) => setMentionsCount(Number(r.data?.count || 0)))
      .catch(() => {});
  }, []);
  useEffect(() => {
    reloadInboxCount();
    reloadMentionsCount();
    const t = setInterval(() => {
      reloadInboxCount();
      reloadMentionsCount();
    }, 30000);
    return () => clearInterval(t);
  }, [reloadInboxCount, reloadMentionsCount]);

  // ── Load conversation list ────────────────────────────────────
  // iter135.10 — silent param avoids the loadingList skeleton flash
  // when the list is re-synced from a WebSocket event. The sidebar
  // already shows the correct optimistic state via bumpListPreview;
  // toggling loadingList=true would just flash a row of skeletons
  // for ~50 ms on every incoming message — perceptible UI hitch.
  const reloadList = useCallback((opts) => {
    const silent = opts?.silent === true;
    if (!silent) setLoadingList(true);
    const url = showClosed
      ? "/conv/conversations?limit=50&include_archived=true"
      : "/conv/conversations?limit=50";
    api.get(url)
      .then((r) => convStore.setConvs(r.data?.items || []))
      .catch(() => { if (!silent) convStore.setConvs([]); })
      .finally(() => { if (!silent) setLoadingList(false); });
  }, [showClosed]);
  // iter135.10 — debounced silent reload. Bursts of message_new
  // / edited / recalled events from a fast-typing partner used to
  // fire a fresh ``GET /conversations`` per event (with skeleton
  // flash + 50-row re-render). We coalesce them into one trailing
  // call 1.5 s after the LAST event lands. The sidebar still feels
  // realtime because ``bumpListPreview`` already nudged the row to
  // the top with the new preview text on every event — this trailing
  // sync only fixes server-authoritative fields like unread_count.
  const listReloadTimerRef = useRef(null);
  const scheduleListReload = useCallback(() => {
    if (listReloadTimerRef.current) clearTimeout(listReloadTimerRef.current);
    listReloadTimerRef.current = setTimeout(() => {
      listReloadTimerRef.current = null;
      reloadList({ silent: true });
    }, 1500);
  }, [reloadList]);
  useEffect(() => () => {
    if (listReloadTimerRef.current) clearTimeout(listReloadTimerRef.current);
  }, []);
  useEffect(() => { reloadList(); }, [reloadList]);

  // ── Load selected conversation + messages ─────────────────────
  // iter134.3 — offline-first read path:
  //   1. Synchronously hydrate ``conv`` from sidebar list metadata
  //      (instant title/header).
  //   2. Synchronously hydrate ``messages`` from the localStorage
  //      cache so the user sees real bubbles in the same React tick.
  //   3. Fire the HTTP load in the background; reconcile state +
  //      cache when it returns.
  // The user only ever sees a blank message area on first-ever open
  // of a conversation that has no cache entry.
  //
  // iter134.8 — optimistic-send compat: messages with ``_optimistic``
  // or ``_failed`` flags are drafts that haven't been persisted
  // server-side yet, so they MUST NOT be written into the cache —
  // a refresh would resurrect a phantom row that no one else can see.
  const loadConversation = useCallback((cid, seed) => {
    if (!cid) { setConv(null); return; }
    // Reset the auto-scroll anchor so the bottom-snap fires for the
    // newly-loaded conversation regardless of cache-vs-fresh path.
    lastMsgIdRef.current = null;
    // First scroll for a freshly-opened conversation must be INSTANT
    // (no smooth animation) — animating from the top of a long
    // history feels jarring. iter134.4.
    firstScrollRef.current = true;
    if (seed) {
      setConv({
        conversation_id: cid,
        title: seed.title || "",
        archived: !!seed.archived,
        last_message_at: seed.last_message_at || null,
        pcs: [],
        active_users: [],
        created_by: null,
        my_customer_id: null,
        _stub: true,
      });
    }
    // Cache hydration — instant if available.
    const cached = msgCacheGet(cid);
    convStore.setMessages(cid, cached || []);
    setHasMoreHistory(true);
    setLoadingConv(true);
    // iter136.7 — hydrate the IDB deep-history cache in parallel.
    // The localStorage cache above only holds the most recent 50;
    // IDB carries up to 500/conv so the scroll-up paginator can
    // serve cache hits well past the first page without ever
    // hitting the network. This runs alongside the HTTP fetch
    // below and reconciles via the same store.setMessages path.
    msgIDB.getAll(cid).then((idbMsgs) => {
      if (!idbMsgs || idbMsgs.length === 0) return;
      // Don't clobber what setMessages just placed — merge instead.
      // The store dedupes by message_id, so prepend is safe.
      const haveIds = new Set((cached || []).map((m) => m.message_id));
      const extras = idbMsgs.filter((m) => !haveIds.has(m.message_id));
      if (extras.length > 0) convStore.prependMessages(cid, extras);
    }).catch(() => { /* best-effort */ });
    Promise.all([
      api.get(`/conv/conversations/${cid}`),
      api.get(`/conv/conversations/${cid}/messages?limit=50`),
    ])
      .then(([c, m]) => {
        setConv(c.data);
        const items = m.data?.items || [];
        convStore.setMessages(cid, items);
        // Confirmed (non-optimistic) only — see iter134.8 note above.
        const confirmed = items.filter((x) => !x._optimistic && !x._failed);
        msgCacheSet(cid, confirmed);
        // iter136.7 — write through to IDB so next conv-open has
        // everything we just rendered, AND so scroll-up cache hits
        // can land instantly.
        msgIDB.putAll(cid, confirmed).catch(() => {});
        // If we got fewer than the requested page, there's no older
        // history to fetch — stop the scroll paginator early.
        if (items.length < 50) setHasMoreHistory(false);
      })
      .catch(() => {
        // Keep cached messages on the screen; only clear ``conv`` so
        // the empty-state pane shows if the conv truly disappeared.
        if (!cached) convStore.setMessages(cid, []);
        setConv(null);
      })
      .finally(() => setLoadingConv(false));
  }, []);
  useEffect(() => {
    if (!selectedId) { setConv(null); return; }
    // If we already have a non-stub conv for this id (e.g. user clicked
    // back to the same row), don't refetch.
    setConv((prev) => {
      if (prev && prev.conversation_id === selectedId && !prev._stub) return prev;
      const seed = conversations.find((c) => c.conversation_id === selectedId);
      loadConversation(selectedId, seed);
      return prev;
    });
    // We intentionally exclude `conversations` from deps — re-running
    // the loader on every list refresh would cause a refetch storm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, loadConversation]);

  // ── Auto-scroll to bottom on new message ──────────────────────
  // Triggers only when the LAST message changes — so prepending
  // older history via the scroll paginator doesn't yank the user
  // back to the bottom. iter134.3.
  //
  // iter134.4 — first paint (cache-hydrate or fresh load) snaps
  // INSTANTLY to the bottom; subsequent incoming messages animate.
  // Without this, opening a long conversation made the browser
  // smooth-scroll from the top of the message list, taking 200-500ms
  // to settle on the latest message.
  //
  // Image-load grace: messages with attached images expand the
  // scroll height AFTER our first snap, leaving the user halfway
  // through the timeline. We re-snap a handful of times during the
  // first ~2 s to absorb late-arriving image loads. The re-snap is
  // cancelled the moment the user scrolls manually (detected via
  // ``onMessagesScroll``) so we never fight the user's intent.
  const snapToBottom = useCallback((behavior = "auto") => {
    // iter136.1 — single source of truth for snap-to-bottom. We used
    // to also call ``el.scrollTop = scrollHeight`` and
    // ``messagesEndRef.scrollIntoView`` here — those fought Virtuoso's
    // own scroll controller and produced visible jitter when the user
    // scrolled near the bottom (each path re-snapped on top of the
    // others). Letting Virtuoso own the scroll eliminates the
    // race entirely.
    messageListRef.current?.scrollToBottom(behavior);
  }, []);
  const userInterruptedScrollRef = useRef(false);
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].message_id : null;
    if (!lastId || lastId === lastMsgIdRef.current) return;
    const isFirst = firstScrollRef.current;
    const wasAtBottom = atBottomRef.current;
    lastMsgIdRef.current = lastId;
    firstScrollRef.current = false;
    // iter136.1 — only force-snap on FIRST open of a conversation.
    // For incoming messages, defer to Virtuoso's ``followOutput``
    // (configured to follow only when isAtBottom). Forcibly snapping
    // here used to drag the viewport down even when the user was
    // reading older history — and combined with the DOM-mutation
    // path above produced the bottom-jitter.
    if (isFirst) {
      snapToBottom("auto");
      // Re-snap a few times to absorb image-load layout shifts on
      // first open, but ABORT immediately if the user scrolls.
      userInterruptedScrollRef.current = false;
      const timers = [80, 250, 600, 1200].map((ms) =>
        setTimeout(() => {
          if (userInterruptedScrollRef.current) return;
          if (!atBottomRef.current) return;
          snapToBottom("auto");
        }, ms),
      );
      return () => timers.forEach(clearTimeout);
    }
    // For subsequent messages: snap only if the user was already at
    // the bottom. We use a short ``requestAnimationFrame`` so the new
    // bubble has had a chance to mount + measure before we scroll.
    // iter136.3 — DON'T re-check atBottomRef inside the rAF: by the
    // time it fires, Virtuoso has already reported atBottom=false
    // because the appended row pushed past the viewport. The
    // ``wasAtBottom`` snapshot taken before the layout shift is the
    // authoritative signal.
    if (wasAtBottom) {
      requestAnimationFrame(() => {
        snapToBottom("smooth");
      });
    }
    return undefined;
  }, [messages, snapToBottom]);

  // ── Scroll-up paginator ───────────────────────────────────────
  // iter135.15 — wired to Virtuoso's ``startReached`` event. Virtuoso
  // auto-preserves scroll anchor when items are prepended so the
  // user's reading position doesn't jump — we no longer need the
  // manual scrollHeight diff.
  const loadOlder = useCallback(() => {
    if (!selectedId || loadingMore || !hasMoreHistory || messages.length === 0) return;
    const oldestSeq = messages[0].sequence_number;
    if (!oldestSeq) return;
    setLoadingMore(true);
    // iter136.7 — try the IndexedDB deep-history cache first. If
    // we hit a non-empty range, prepend without any HTTP round-trip.
    // Only when the cache is dry below ``oldestSeq`` do we fall
    // through to the server.
    msgIDB.getOlderRange(selectedId, { beforeSeq: oldestSeq, limit: 50 })
      .then((cachedOlder) => {
        if (cachedOlder && cachedOlder.length > 0) {
          convStore.prependMessages(selectedId, cachedOlder);
          // If the cache returned fewer than we asked for, the
          // floor of our local archive lies inside this batch.
          // We DON'T flip ``hasMoreHistory`` to false here — we
          // only know the cache is dry, not the server. Next
          // scroll-up triggers the HTTP path below.
          setLoadingMore(false);
          return null;
        }
        return api.get(
          `/conv/conversations/${selectedId}/messages?before_seq=${oldestSeq}&limit=50`,
        );
      })
      .then((r) => {
        if (!r) return;  // IDB hit — already handled above.
        const older = r.data?.items || [];
        if (older.length === 0) {
          setHasMoreHistory(false);
          return;
        }
        if (older.length < 50) setHasMoreHistory(false);
        convStore.prependMessages(selectedId, older);
        // Write through to IDB so the next scroll-up serves cache.
        msgIDB.putAll(
          selectedId,
          older.filter((x) => !x._optimistic && !x._failed),
        ).catch(() => {});
      })
      .catch(() => { /* silent — user can scroll again to retry */ })
      .finally(() => setLoadingMore(false));
  }, [selectedId, loadingMore, hasMoreHistory, messages]);

  const onAtBottomChange = useCallback((atBottom) => {
    atBottomRef.current = atBottom;
    if (atBottom) {
      userInterruptedScrollRef.current = false;
      setShowJumpToBottom(false);
    } else {
      userInterruptedScrollRef.current = true;
      setShowJumpToBottom(true);
    }
  }, []);

  // ── Typing indicator + read-receipts state ────────────────────
  // iter135.11 — typing + read receipts now live in the conv store.
  // Setters are replaced by ``convStore.setTyping(...)`` and
  // ``convStore.setReads(...)`` which only notify components
  // subscribed to those particular slices.

  // Reload typing list (one HTTP call after WS reconnect, then WS pushes update it).
  const reloadTyping = useCallback(() => {
    if (!selectedId) return;
    api.get(`/conv/conversations/${selectedId}/typing`)
      .then((r) => convStore.setTyping(selectedId, r.data?.items || []))
      .catch(() => {});
  }, [selectedId]);

  const reloadReadReceipts = useCallback(() => {
    if (!selectedId) return;
    api.get(`/conv/conversations/${selectedId}/read-receipts`)
      .then((r) => convStore.setReads(selectedId, r.data?.items || []))
      .catch(() => {});
  }, [selectedId]);

  // ── Realtime via WebSocket — replaces the iter122 4s polling ──
  // Track the server's last seq we know about so reconnects can
  // back-fill missed messages with a single GET.
  const lastKnownSeqRef = useRef(0);
  useEffect(() => {
    lastKnownSeqRef.current = messages.length ? messages[messages.length - 1].sequence_number : 0;
  }, [messages]);

  const handleSocketEvent = useCallback((evt) => {
    if (!evt || !evt.type) return;
    const t = evt.type;
    if (t === "hello") {
      // Synced — no-op (initial REST load already populated).
      return;
    }
    if (t === "message_new") {
      // Append, deduping by message_id (the optimistic append from
      // onSend may have beaten the broadcast back to us).
      const newMsg = {
        message_id: evt.message_id,
        client_message_id: evt.client_message_id,
        conversation_id: evt.conversation_id,
        sequence_number: evt.sequence_number,
        sender: evt.sender,
        content_type: evt.content_type,
        content: evt.content,
        server_timestamp: evt.server_timestamp,
        edited: false,
        recalled: false,
        mentions: Array.isArray(evt.mentions) ? evt.mentions : [],
      };
      // iter135.11 — store-driven dedupe + optimistic swap. The
      // store handles "real row already there" silently; if a
      // matching optimistic ghost exists for ``client_message_id``
      // we swap it in place to keep the bubble's identity stable
      // (no flash, no re-mount).
      if (evt.client_message_id) {
        convStore.swapOptimistic(evt.conversation_id, evt.client_message_id, newMsg);
      } else {
        convStore.appendMessage(evt.conversation_id, newMsg);
      }
      // Mirror to the cache so the next conversation-open is instant.
      msgCacheAppend(evt.conversation_id, newMsg);
      // iter136.7 — also write to the IndexedDB deep-history cache.
      msgIDB.putOne(evt.conversation_id, newMsg).catch(() => {});
      // iter134.9 — sidebar optimistic update for incoming messages
      // too, so the preview moves to the top of the list before the
      // ``reloadList()`` HTTP round-trip completes. Mirrors the
      // backend's [Image]/[Recalled]/text rendering rules.
      const previewText = (() => {
        if (newMsg.content_type === "image") return "[Image]";
        if (newMsg.content_type === "video") return "[Video]";
        if (newMsg.content_type === "audio") return "[Audio]";
        if (newMsg.content_type === "file") return "[File]";
        return (newMsg.content || "").slice(0, 140);
      })();
      convStore.bumpConvPreview(evt.conversation_id, {
        last_message_preview: previewText,
        last_message_at: newMsg.server_timestamp,
      });
      scheduleListReload();
      // iter136 — bump the bell-mention badge if THIS user is the
      // direct target, OR the message carries an @everyone/@here.
      // (We don't enforce membership client-side — server will have
      // dropped invalid markers already.)
      try {
        const mlist = newMsg.mentions || [];
        const meKey = me ? `${me.user_type}:${me.user_id}` : "";
        const isSelf = me &&
          evt.sender?.user_type === me.user_type &&
          String(evt.sender?.user_id) === String(me.user_id);
        if (!isSelf && me && mlist.length > 0) {
          const hit = mlist.some((mn) => {
            if (!mn) return false;
            if (mn.kind === "everyone" || mn.kind === "here") return true;
            return mn.kind === "user" && `${mn.user_type}:${mn.user_id}` === meKey;
          });
          if (hit) setMentionsCount((n) => n + 1);
        }
      } catch (_e) { /* badge is best-effort */ }
      return;
    }
    if (t === "message_edited") {
      convStore.patchMessage(evt.conversation_id, evt.message_id, {
        content: evt.content, edited: true,
        ...(Array.isArray(evt.mentions) ? { mentions: evt.mentions } : {}),
      });
      msgCachePatch(evt.conversation_id, evt.message_id, {
        content: evt.content, edited: true,
      });
      // iter136.7 — write through to IDB.
      msgIDB.patchOne(evt.conversation_id, evt.message_id, {
        content: evt.content, edited: true,
        ...(Array.isArray(evt.mentions) ? { mentions: evt.mentions } : {}),
      }).catch(() => {});
      // iter134.6 — sidebar preview must reflect the new content
      // when the LATEST message is the one being edited.
      scheduleListReload();
      return;
    }
    if (t === "message_recalled") {
      convStore.patchMessage(evt.conversation_id, evt.message_id, {
        recalled: true, content: "",
      });
      msgCachePatch(evt.conversation_id, evt.message_id, {
        recalled: true, content: "",
      });
      // iter136.7 — write through to IDB.
      msgIDB.patchOne(evt.conversation_id, evt.message_id, {
        recalled: true, content: "",
      }).catch(() => {});
      // iter134.9 — only update the sidebar preview when the
      // recalled message was the LATEST in this conversation. Older
      // recalls leave the preview unchanged (a deeper history
      // recall doesn't change the visible last-message line).
      const c = convStore.getConv(evt.conversation_id);
      if (c && c.last_message_preview !== "[Recalled]") {
        convStore.patchConv(evt.conversation_id, { last_message_preview: "[Recalled]" });
      }
      scheduleListReload();
      return;
    }
    if (t === "typing") {
      // Skip our own ticks.
      if (me && evt.user_type === me.user_type && String(evt.user_id) === String(me.user_id)) return;
      convStore.upsertTyping(
        evt.conversation_id, evt.user_type, evt.user_id, !!evt.is_typing,
      );
    }
  }, [me, scheduleListReload]);

  const { connected: wsConnected } = useGlobalConvSocket(handleSocketEvent);

  // After (re)connect, back-fill any messages the WS missed during
  // the gap, plus refresh typing + read-receipts state.
  useEffect(() => {
    if (!selectedId || !wsConnected) return;
    const after = lastKnownSeqRef.current;
    api.get(`/conv/conversations/${selectedId}/messages?after_seq=${after}&limit=200`)
      .then((r) => {
        const newOnes = r.data?.items || [];
        for (const m of newOnes) {
          // ``appendMessage`` is a no-op when the row is already
          // present, so this safely back-fills only the gap.
          convStore.appendMessage(selectedId, m);
        }
      })
      .catch(() => {});
    reloadTyping();
    reloadReadReceipts();
  }, [selectedId, wsConnected, reloadTyping, reloadReadReceipts]);

  // ── Background presence heartbeat (60s, much lighter than iter122)
  // iter136.16 — silent: true so transient network blips during a
  // screen-share don't surface a toast on every missed heartbeat.
  useEffect(() => {
    if (!selectedId) return undefined;
    api.post(`/conv/conversations/${selectedId}/presence`, undefined, { silent: true }).catch(() => {});
    const t = setInterval(() => {
      api.post(`/conv/conversations/${selectedId}/presence`, undefined, { silent: true }).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [selectedId]);

  // ── Cull stale typing rows every 2s ───────────────────────────
  useEffect(() => {
    if (!typingUsers.length || !selectedId) return undefined;
    const t = setInterval(() => {
      convStore.cullTyping(selectedId, 6000);
    }, 2000);
    return () => clearInterval(t);
  }, [typingUsers.length, selectedId]);

  // ── Mark read on load ─────────────────────────────────────────
  // iter135.10 — debounced + dedup'd. The previous version POSTed
  // /read + GETted /read-receipts on EVERY ``messages.length``
  // change — i.e., once per send AND once per incoming message.
  // With a burst of 5 messages in 2 s that's 10 HTTP round-trips
  // on the critical path of "is the chat responsive". We coalesce
  // with a 700 ms trailing debounce, AND skip the POST when the
  // last-seq we already acked equals the current last-seq (prevents
  // optimistic-then-broadcast pairs from each firing a POST).
  // The ``reloadReadReceipts`` chained behind every read POST has
  // also been removed — receipts only need refreshing on
  // conversation-open and WS-reconnect (both already handled).
  const markReadTimerRef = useRef(null);
  const lastAckedSeqRef = useRef(0);
  // iter135.20 — "N new messages" divider anchor. We snapshot the
  // conversation's unread_count at the moment the user selects it;
  // the divider then renders above the first unread message and
  // STAYS there for the session (doesn't shift down as the user
  // reads further) — matches WhatsApp / Slack behaviour. Cleared
  // when the user switches away or when unread_count was 0 at open
  // time (no divider shown).
  const [unreadAnchorSeq, setUnreadAnchorSeq] = useState(null);
  useEffect(() => {
    if (!selectedId) { setUnreadAnchorSeq(null); return; }
    const c = conversations.find((x) => x.conversation_id === selectedId);
    const unread = c?.unread_count || 0;
    if (unread <= 0) { setUnreadAnchorSeq(null); return; }
    // Derive the first-unread sequence via the tail of the loaded
    // messages — ``unread`` is the count of un-read non-system
    // messages from other senders, so the (N-unread)-th message
    // from the end is the last-read one; the one after is the
    // first-unread. Only runs on conversation-select (deps array
    // excludes ``messages`` on purpose to lock the anchor).
    // Fall back to ``null`` while messages are still loading; the
    // next effect run (after ``messages`` populates for this convId)
    // will lock the anchor.
    if (!messages.length) return;
    const nonSystem = messages.filter(
      (m) => m.content_type !== "system" && !m.event_type,
    );
    if (nonSystem.length < unread) {
      // Older-history load hasn't caught up yet — punt; effect re-
      // runs on next ``messages.length`` change (via the fallback
      // effect below) OR when the user pages up.
      return;
    }
    const firstUnread = nonSystem[nonSystem.length - unread];
    if (firstUnread?.sequence_number) {
      setUnreadAnchorSeq(firstUnread.sequence_number);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  // One-shot re-compute once messages arrive for the newly-selected
  // conv (the effect above runs before the initial fetch resolves).
  useEffect(() => {
    if (!selectedId || unreadAnchorSeq !== null) return;
    const c = conversations.find((x) => x.conversation_id === selectedId);
    const unread = c?.unread_count || 0;
    if (unread <= 0 || !messages.length) return;
    const nonSystem = messages.filter(
      (m) => m.content_type !== "system" && !m.event_type,
    );
    if (nonSystem.length < unread) return;
    const firstUnread = nonSystem[nonSystem.length - unread];
    if (firstUnread?.sequence_number) {
      setUnreadAnchorSeq(firstUnread.sequence_number);
    }
  }, [selectedId, messages.length, unreadAnchorSeq, conversations, messages]);
  useEffect(() => {
    if (!selectedId || !messages.length) return undefined;
    const lastSeq = messages[messages.length - 1].sequence_number;
    // Skip when we've already acked this exact seq (or beyond).
    if (lastSeq && lastSeq <= lastAckedSeqRef.current) return undefined;
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      // iter135.12 — over WS instead of HTTP. The server-side
      // handler (``conversation_ws._handle_inbound_read``) calls
      // the same ``svc.mark_conversation_read`` so the contract is
      // identical — just a much cheaper transport.
      convSocket.sendRead(selectedId, lastSeq);
      lastAckedSeqRef.current = lastSeq;
      // iter136 — refresh the bell-mention badge after a mark-read.
      reloadMentionsCount();
    }, 700);
    return () => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    };
  }, [selectedId, messages.length]);
  // Reset the acked-seq tracker when the user switches conversations
  // — the per-conv high-water mark doesn't carry across selections.
  useEffect(() => { lastAckedSeqRef.current = 0; }, [selectedId]);

  // ── Typing tick (debounced) ───────────────────────────────────
  const typingTickRef = useRef(0);
  // iter136 — Mention picker state. ``mentionState`` is null when
  // the picker is closed; otherwise carries the trigger position
  // (caret index of the ``@``), the current query (text after it),
  // and the anchor (viewport coords of the trigger) for placement.
  const composerRef = useRef(null);
  const [mentionState, setMentionState] = useState(null);

  const computeMentionState = useCallback((value, caret, el) => {
    if (!el || caret == null) return null;
    // Walk backwards from the caret to find an ``@`` that's either
    // at index 0 or preceded by whitespace. If we hit a whitespace
    // character before that, the picker should be closed.
    let i = caret - 1;
    while (i >= 0) {
      const ch = value[i];
      if (ch === "@") {
        if (i === 0 || /\s/.test(value[i - 1])) {
          const query = value.slice(i + 1, caret);
          // Bail out if the typed query already contains whitespace
          // (the user committed past the mention without selecting).
          if (/\s/.test(query)) return null;
          // Bail out if the typed segment looks like an already-
          // committed marker tail "name](type:id)".
          if (query.includes("]")) return null;
          // Caret coords — best-effort using a measuring proxy.
          const rect = el.getBoundingClientRect();
          return {
            triggerIdx: i,
            query,
            anchor: {
              top: rect.top + 4,
              left: rect.left + 16,
            },
          };
        }
        return null;
      }
      if (/\s/.test(ch)) return null;
      i -= 1;
    }
    return null;
  }, []);

  const onComposerChange = (e) => {
    const v = e.target.value;
    setComposerValue(v);
    const now = Date.now();
    if (selectedId && v.length > 0 && (now - typingTickRef.current) > 3000) {
      typingTickRef.current = now;
      // iter135.12 — typing tick over WS, no HTTP round-trip.
      convSocket.sendTyping(selectedId, true);
    }
    if (selectedId && v.length === 0) {
      convSocket.sendTyping(selectedId, false);
    }
    // iter136 — recompute mention picker state on every keystroke.
    setMentionState(
      computeMentionState(v, e.target.selectionStart, e.target),
    );
  };

  const onMentionSelect = useCallback((token, _label) => {
    if (!mentionState) return;
    setComposerValue((prev) => {
      const el = composerRef.current;
      const caret = el?.selectionStart ?? prev.length;
      const before = prev.slice(0, mentionState.triggerIdx);
      const after = prev.slice(caret);
      // Always append a trailing space for clean continued typing.
      const next = `${before}${token} ${after}`;
      // Restore caret after the inserted token + space on the next tick.
      requestAnimationFrame(() => {
        if (!el) return;
        const newCaret = before.length + token.length + 1;
        el.focus();
        try { el.setSelectionRange(newCaret, newCaret); } catch (_e) {}
      });
      return next;
    });
    setMentionState(null);
  }, [mentionState]);

  // iter134.10 — Paste image from clipboard (Cmd/Ctrl+V on a screenshot,
  // copy-image-from-app, etc). Looks for any clipboard item whose type
  // starts with ``image/``, normalises its filename to
  // ``pasted-{timestamp}.{ext}`` (clipboard images come without a
  // real name), and routes it through the existing optimistic-upload
  // flow. Falls through to the default text paste if no image is
  // found, so pasting URLs / multi-line snippets still works.
  const onComposerPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && (it.type || "").startsWith("image/")) {
        const blob = it.getAsFile();
        if (!blob) continue;
        e.preventDefault();
        const ext = (blob.type.split("/")[1] || "png").split("+")[0];
        // Wrap the blob in a File so ``upload`` form-data filename is
        // sensible. Some browsers already return File subclass; the
        // ``new File`` constructor handles both.
        const named = new File([blob], `pasted-${Date.now()}.${ext}`, {
          type: blob.type, lastModified: Date.now(),
        });
        onUploadAndSend(named);
        return;
      }
    }
    // No image in clipboard → let the textarea handle the paste
    // normally (text, links, multi-line snippets all unaffected).
  };

  // iter134.11 — Drag-and-drop into the chat pane. Mirrors the paste
  // flow but for files dragged from Finder / Explorer / browser.
  // ``dragDepth`` tracks nested dragenter/dragleave events so the
  // overlay doesn't flicker as the user moves over child elements
  // (Chrome fires dragleave on every nested boundary).
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const dropAccepted = (e) => {
    // Reject the drop if no actual files are present (e.g. dragging
    // selected text from another tab) so we don't preventDefault
    // browser-native operations like dropping a URL.
    const dt = e.dataTransfer;
    if (!dt) return false;
    if (Array.from(dt.types || []).includes("Files")) return true;
    return (dt.files && dt.files.length > 0);
  };
  const onPaneDragEnter = (e) => {
    if (!dropAccepted(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };
  const onPaneDragOver = (e) => {
    if (!dropAccepted(e)) return;
    // Without preventDefault here the browser won't fire ``drop``.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onPaneDragLeave = (e) => {
    if (!dropAccepted(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  };
  const onPaneDrop = (e) => {
    if (!dropAccepted(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (conv?.archived) {
      toast.error(t("convv2.error_conv_closed"));
      return;
    }
    // Loop through every dropped file. Each one goes through the
    // same optimistic-upload flow as the attach button + paste, so
    // multi-file drops show one bubble per file.
    const files = Array.from(e.dataTransfer.files || []);
    for (const f of files) onUploadAndSend(f);
  };

  // ── Actions ───────────────────────────────────────────────────
  // iter134.8 — fully optimistic send. Bubble appears INSTANTLY with
  // a sending indicator; input is cleared and re-enabled in the same
  // tick so the user can keep typing the next message while the POST
  // is still in flight. On success the temp bubble is reconciled with
  // the server's authoritative row. On failure it stays in the
  // timeline with a red retry affordance — the user never loses what
  // they typed. Dedup with the WS broadcast happens via
  // ``client_message_id`` (iter134.8 backend addition).
  const onSend = () => {
    const text = composerValue.trim();
    if (!text || !selectedId) return;
    const cmid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const tempId = `temp-${cmid}`;
    const tempMsg = {
      message_id: tempId,
      client_message_id: cmid,
      conversation_id: selectedId,
      sequence_number: Number.MAX_SAFE_INTEGER, // sort to bottom
      sender: {
        user_type: me?.user_type, user_id: me?.user_id,
        customer_id: me?.customer_id,
        name: me?.name, company_name: me?.customer_name,
      },
      content_type: "text",
      content: text,
      server_timestamp: new Date().toISOString(),
      edited: false, recalled: false,
      _optimistic: true, _failed: false,
    };
    // Synchronous: append + clear composer + cancel typing + bump
    // sidebar preview — all before the network request even begins.
    convStore.appendMessage(selectedId, tempMsg);
    setComposerValue("");
    // iter136.3 — explicit snap-to-bottom on the user's OWN send.
    // The post-message useEffect can't be trusted here because
    // Virtuoso's at-bottom state change fires synchronously during
    // the layout that immediately follows ``appendMessage`` — by
    // the time our effect reads ``atBottomRef.current`` it has
    // already flipped to false. The user just hit Send: their
    // intent is unambiguous, so we always pull the view to the
    // bottom across the next two animation frames.
    requestAnimationFrame(() => {
      messageListRef.current?.scrollToBottom("auto");
      requestAnimationFrame(() => {
        messageListRef.current?.scrollToBottom("auto");
      });
    });
    // iter134.9 — sidebar optimistic update so the preview reflects
    // the just-sent text immediately. Without this the user sees
    // their stale "previous" preview hanging around for ~1 s until
    // the server-side ``reloadList()`` returns.
    convStore.bumpConvPreview(selectedId, {
      last_message_preview: text.slice(0, 140),
      last_message_at: tempMsg.server_timestamp,
    });
    api.post(`/conv/conversations/${selectedId}/typing`, { is_typing: false }).catch(() => {});
    // Note: we keep the typing-clear over HTTP here because the
    // send-message POST is still HTTP — keeping them on the same
    // transport guarantees ordering: the typing-clear MUST land
    // before the message broadcast so other clients don't briefly
    // see "still typing…" with a new message.
    // iter135.12 — also send over WS as a belt-and-suspenders.
    convSocket.sendTyping(selectedId, false);
    // Background POST. ``setSending`` is intentionally NOT toggled —
    // the input must remain enabled so the user can keep typing.
    api.post(`/conv/conversations/${selectedId}/messages`, {
      client_message_id: cmid,
      content_type: "text",
      content: text,
    })
      .then((r) => {
        const sent = r.data;
        // iter135.11 — reconcile via the store. ``swapOptimistic``
        // is idempotent: if the WS broadcast already replaced the
        // temp row, the optimistic ghost is gone and we no-op +
        // append the real row (deduped). Otherwise we swap in place.
        const realMsg = {
          ...tempMsg,
          message_id: sent.message_id,
          sequence_number: sent.sequence_number,
          server_timestamp: sent.server_timestamp,
          sender: {
            ...tempMsg.sender,
            customer_id: sent.sender_customer_id,
            name: sent.sender_name || tempMsg.sender.name,
            company_name: sent.sender_company_name || tempMsg.sender.company_name,
          },
          _optimistic: false,
        };
        convStore.swapOptimistic(selectedId, cmid, realMsg);
        // If the temp ghost survived (because swap couldn't find
        // it — broadcast already won), drop the orphan.
        if (convStore.getMessage(selectedId, tempId)) {
          convStore.removeMessage(selectedId, tempId);
        }
        scheduleListReload();
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Send failed");
        convStore.patchMessage(selectedId, tempId, { _failed: true, _optimistic: false });
      });
  };

  // ── Edit / Recall / Attachment helpers ────────────────────────
  const onEditMessage = async (mid, newContent) => {
    const trimmed = (newContent || "").trim();
    if (!trimmed) return;
    try {
      await api.post(`/conv/messages/${mid}/edit`, { content: trimmed });
      convStore.patchMessage(selectedId, mid, { content: trimmed, edited: true });
      toast.success(t("convv2.toast_message_edited", "Message edited"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("convv2.toast_edit_failed", "Edit failed"));
    }
  };

  const onRecallMessage = async (mid) => {
    const ok = await confirm({
      title: t("convv2.recall_dialog_title", "Recall this message?"),
      description: t("convv2.recall_dialog_description", "Recipients will see it as deleted. This can't be undone."),
      confirmLabel: t("convv2.confirm_recall"),
      tone: "danger",
      testId: "conv-recall-confirm",
    });
    if (!ok) return;
    try {
      await api.post(`/conv/messages/${mid}/recall`);
      convStore.patchMessage(selectedId, mid, { recalled: true, content: "" });
      toast.success(t("convv2.toast_message_recalled", "Message recalled"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("convv2.toast_recall_failed", "Recall failed"));
    }
  };

  const onUploadAndSend = async (file) => {
    if (!file || !selectedId) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t("convv2.toast_file_too_large", "File exceeds 25 MB limit"));
      return;
    }
    // iter134.8 — optimistic: append a "uploading…" bubble immediately
    // so the user sees the file leave the composer. The actual upload
    // and send happen in the background. The bubble carries a local
    // object URL preview for images so the thumbnail renders without
    // waiting for the server round-trip.
    const cmid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const tempId = `temp-${cmid}`;
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : "";
    const tempMsg = {
      message_id: tempId,
      client_message_id: cmid,
      conversation_id: selectedId,
      sequence_number: Number.MAX_SAFE_INTEGER,
      sender: {
        user_type: me?.user_type, user_id: me?.user_id,
        customer_id: me?.customer_id,
        name: me?.name, company_name: me?.customer_name,
      },
      content_type: isImage ? "image" : "file",
      content: previewUrl || file.name,
      server_timestamp: new Date().toISOString(),
      edited: false, recalled: false,
      _optimistic: true, _failed: false,
      _uploading: true, _filename: file.name,
    };
    convStore.appendMessage(selectedId, tempMsg);
    // iter134.9 — sidebar optimistic preview ("[Image]" / "[File]").
    convStore.bumpConvPreview(selectedId, {
      last_message_preview: isImage ? "[Image]" : "[File]",
      last_message_at: tempMsg.server_timestamp,
    });
    try {
      const fd = new FormData();
      fd.append("upload", file, file.name);
      const up = await api.post(
        `/conv/conversations/${selectedId}/uploads`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const send = await api.post(`/conv/conversations/${selectedId}/messages`, {
        client_message_id: cmid,
        content_type: up.data.content_type,    // "image" or "file"
        content: up.data.url,                  // /api/conv/uploads/<id>
      });
      const realMsg = {
        ...tempMsg,
        message_id: send.data.message_id,
        sequence_number: send.data.sequence_number,
        server_timestamp: send.data.server_timestamp,
        content_type: up.data.content_type,
        content: up.data.url,
        sender: {
          ...tempMsg.sender,
          customer_id: send.data.sender_customer_id,
          name: send.data.sender_name || tempMsg.sender.name,
          company_name: send.data.sender_company_name || tempMsg.sender.company_name,
        },
        _optimistic: false, _uploading: false,
      };
      convStore.swapOptimistic(selectedId, cmid, realMsg);
      if (convStore.getMessage(selectedId, tempId)) {
        convStore.removeMessage(selectedId, tempId);
      }
      // Free the local object URL — the server-cached URL takes over.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      scheduleListReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      convStore.patchMessage(selectedId, tempId, {
        _failed: true, _uploading: false, _optimistic: false,
      });
    }
  };

  const onInitiate = async (target_customer_id) => {
    try {
      const r = await api.post("/conv/conversations", { target_customer_id });
      setShowInitiate(false);
      reloadList();
      setSearchParams({ c: r.data.conversation_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Initiate failed");
    }
  };

  const onRequestInitiate = async (target_customer_id) => {
    try {
      const r = await api.post("/conv/requests/initiate-upstream", { target_customer_id });
      setShowInitiate(false);
      toast.success(`Invite sent — waiting for an Admin at #${target_customer_id} to accept (request ${r.data?.request_id?.slice(0, 8)}…)`);
      reloadInboxCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const onAddCompany = async (new_customer_id) => {
    try {
      const r = await api.post(`/conv/conversations/${selectedId}/companies`, { new_customer_id });
      setShowAddCompany(false);
      toast.success(`Added to Conversation: ${new_customer_id}`);
      loadConversation(selectedId);
      void r;
    } catch (e) {
      const detail = e?.response?.data?.detail || t("convv2.error_add_failed");
      // Apply-Up hint: backend returns 400 with this exact phrasing
      // when the chosen Company is an ancestor (descendant of nothing
      // currently in the PCS). The picker offers a fallback.
      if (/Apply-Up add requires acceptance/i.test(detail)) {
        const ok = await confirm({
          title: `Send Apply-Up request to #${new_customer_id}?`,
          description: `${detail}\n\nA User in that Company will need to accept before they're added.`,
          confirmLabel: t("convv2.confirm_send_request"),
          testId: "conv-applyup-fallback-confirm",
        });
        if (!ok) return;
        try {
          const rr = await api.post(
            `/conv/conversations/${selectedId}/companies/request`,
            { target_customer_id: new_customer_id },
          );
          setShowAddCompany(false);
          toast.success(`Apply-Up request submitted (id ${rr.data?.request_id?.slice(0, 8)}…)`);
          loadConversation(selectedId);
        } catch (ee) {
          toast.error(ee?.response?.data?.detail || "Apply-Up submit failed");
        }
        return;
      }
      toast.error(detail);
    }
  };

  const onRequestCompany = async (target_customer_id) => {
    try {
      const rr = await api.post(
        `/conv/conversations/${selectedId}/companies/request`,
        { target_customer_id },
      );
      setShowAddCompany(false);
      toast.success(`Invite sent (request id ${rr.data?.request_id?.slice(0, 8)}…)`);
      loadConversation(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const onJoin = async () => {
    try { await api.post(`/conv/conversations/${selectedId}/join`); loadConversation(selectedId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Join failed"); }
  };
  const onLeave = async () => {
    try { await api.post(`/conv/conversations/${selectedId}/leave`, { reason: "voluntary" }); loadConversation(selectedId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Leave failed"); }
  };

  // iter134 — optimistic close. Update the local list IMMEDIATELY so
  // the user doesn't pay another full ``reloadList()`` round-trip after
  // the POST returns. The server is the source of truth — we still
  // refetch in the background to reconcile, but the UI is responsive.
  const onClose = async () => {
    if (!selectedId) return;
    const ok = await confirm({
      title: t("convv2.close_dialog_title", "Close this conversation?"),
      message: t("convv2.close_dialog_message", "Once closed, no new messages can be sent. The thread will move to the archived list and remain auditable."),
      confirmLabel: t("convv2.confirm_close_conv"),
      destructive: true,
    });
    if (!ok) return;
    const cidToClose = selectedId;
    // Optimistic: mark as archived immediately.
    convStore.patchConv(cidToClose, { archived: true });
    setShowConvMenu(false);
    setSearchParams({});
    try {
      await api.post(`/conv/conversations/${cidToClose}/close`);
      toast.success(t("convv2.toast_conversation_closed", "Conversation closed"));
      // Background refetch — silent on success, only logs on failure.
      reloadList();
    } catch (e) {
      // Roll back: refetch authoritative state from the server.
      toast.error(e?.response?.data?.detail || t("convv2.toast_close_failed", "Close failed"));
      reloadList();
    }
  };

  // The "Close" button is visible only to (a) staff or (b) a person
  // whose CURRENT Company == ``conv.created_by.customer_id``. The
  // server enforces the same gate; the UI hide is purely cosmetic.
  const canClose = useMemo(() => {
    if (!conv || !me || conv.archived) return false;
    if (me.user_type === "staff") return true;
    return (
      me.user_type === "person" &&
      conv.created_by &&
      String(me.customer_id) === String(conv.created_by.customer_id)
    );
  }, [conv, me]);

  // iter135 — screen-share lifecycle.
  const shareState = useShareState(selectedId);

  // ── Filtering ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = conversations;
    // Belt-and-braces filter: even though the server respects
    // ``include_archived``, the cached response from the previous
    // tab can briefly leak through during the toggle. Always filter
    // to the tab's invariant locally too. iter133 testing-agent fix.
    list = list.filter((c) => (showClosed ? c.archived : !c.archived));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      (c.title || "").toLowerCase().includes(q) ||
      (c.last_message_preview || "").toLowerCase().includes(q)
    );
  }, [conversations, search, showClosed]);

  const isActiveInThisConv = useMemo(() => {
    if (!conv || !me) return false;
    return (conv.active_users || []).some(
      (u) => u.user_type === me.user_type && String(u.user_id) === String(me.user_id)
    );
  }, [conv, me]);

  return (
    <div className="h-screen flex flex-col" style={{ background: "#f0f2f5" }} data-testid="conversations-v2-page">
      {/* Header — slim white bar, WhatsApp-Web style */}
      <header className="h-12 px-3 md:px-6 flex items-center gap-3 shrink-0 bg-white border-b border-slate-200">
        <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={t("convv2.tooltip_home", "Home")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <ChatCircle size={18} weight="fill" style={{ color: "#00a884" }} />
        <h1 className="text-sm md:text-base font-black tracking-tight font-['Chivo']">{t("convv2.title_hdr")}</h1>
        {me && (
          <span className="text-[11px] text-slate-400 font-mono hidden sm:inline truncate max-w-[300px]">
            {me.customer_name}
          </span>
        )}
        <div className="ml-auto shrink-0 flex items-center gap-2">
          <button
            data-testid="conv-mentions-bell"
            onClick={() => {
              // No dedicated panel yet — clicking jumps the user to
              // their first conversation with unread mentions and
              // refreshes the badge.
              if (mentionsCount > 0) {
                api.get("/conv/mentions/unread-count")
                  .then((r) => {
                    const list = r.data?.conversations || [];
                    if (list[0]?.conversation_id) {
                      setSearchParams({ c: list[0].conversation_id });
                    }
                  })
                  .catch(() => {});
              }
            }}
            className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-amber-50 text-amber-600 transition-colors"
            title={
              mentionsCount === 1
                ? t("convv2.mentions_badge_title_one")
                : t("convv2.mentions_badge_title").replace("{count}", String(mentionsCount))
            }
          >
            <At size={17} weight="bold" />
            {mentionsCount > 0 && (
              <span
                data-testid="conv-mentions-badge"
                className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-white text-[9px] font-bold"
                style={{ background: "#f59e0b" }}
              >
                {mentionsCount > 99 ? "99+" : mentionsCount}
              </span>
            )}
          </button>
          <button
            data-testid="conv-inbox-btn"
            onClick={() => setShowInbox(true)}
            className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
            title={t("convv2.btn_inbox")}
          >
            <Bell size={16} weight="bold" />
            {inboxCount > 0 && (
              <span
                data-testid="conv-inbox-badge"
                className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-white text-[9px] font-bold"
                style={{ background: "#00a884" }}
              >
                {inboxCount}
              </span>
            )}
          </button>
          <button
            data-testid="conv-notif-prefs-btn"
            onClick={() => setShowNotifPrefs(true)}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
            title={t("notif.title", "Notification preferences")}
          >
            <Gear size={16} weight="bold" />
          </button>
          <button
            data-testid="conv-initiate-btn"
            onClick={() => setShowInitiate(true)}
            className="h-9 px-3 flex items-center gap-1.5 rounded-full text-white text-xs font-bold uppercase tracking-wider hover:brightness-95 transition"
            style={{ background: "#00a884" }}
          >
            <Plus size={13} weight="bold" />
            <span className="hidden sm:inline">{t("convv2.action_new")}</span>
          </button>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — WhatsApp-Web list pane */}
        <aside
          data-testid="conv-sidebar"
          className={`flex-col bg-white border-r border-slate-200 w-full md:w-[380px] shrink-0 ${selectedId ? "hidden md:flex" : "flex"}`}
        >
          {/* Search — pill input on the same #f0f2f5 strip */}
          <div className="px-3 py-2 shrink-0" style={{ background: "#f0f2f5" }}>
            <div className="relative">
              <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("convv2.search_placeholder")}
                data-testid="conv-search"
                className="w-full pl-9 pr-3 h-9 rounded-full border border-slate-200 text-sm bg-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            {/* iter133 — "Show closed" toggle. Two pill buttons — Active
                (default, only live conversations) vs Closed (archived
                read-only timeline). Keeps the audit trail one click
                away without polluting the active inbox. */}
            <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider">
              <button
                onClick={() => setShowClosed(false)}
                data-testid="conv-tab-active"
                className={`px-2.5 py-1 rounded-full transition ${
                  !showClosed
                    ? "text-white"
                    : "text-slate-500 hover:bg-slate-200"
                }`}
                style={!showClosed ? { background: "#00a884" } : undefined}
              >
                Active
              </button>
              <button
                onClick={() => setShowClosed(true)}
                data-testid="conv-tab-closed"
                className={`px-2.5 py-1 rounded-full transition ${
                  showClosed
                    ? "text-white"
                    : "text-slate-500 hover:bg-slate-200"
                }`}
                style={showClosed ? { background: "#667781" } : undefined}
              >
                Closed
              </button>
              {loadingList && (
                <CircleNotch
                  size={14}
                  weight="bold"
                  className="ml-auto animate-spin text-slate-400"
                  data-testid="conv-list-loading"
                  aria-label={t("convv2.aria_loading_list", "Loading conversations")}
                />
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="conv-list">
            {!loadingList && filtered.length === 0 && (
              <div className="text-center py-16 text-slate-500 px-6">
                <ChatCircle size={36} weight="duotone" className="text-slate-300 mx-auto mb-3" />
                {showClosed ? (
                  <>
                    <div className="text-sm font-medium">{t("convv2.sidebar_empty_closed")}</div>
                    <div className="text-xs mt-1 text-slate-400">{t("convv2.closed_archive_hint", "Closed threads will appear here for audit reference.")}</div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium">{t("convv2.sidebar_empty_none")}</div>
                    <div className="text-xs mt-1 text-slate-400">{t("convv2.sidebar_empty_hint")}</div>
                  </>
                )}
              </div>
            )}
            {filtered.map((c) => (
              <SidebarRow
                key={c.conversation_id}
                convId={c.conversation_id}
                selected={selectedId === c.conversation_id}
                onSelect={(id) => setSearchParams({ c: id })}
              />
            ))}
          </div>
        </aside>

        {/* Right pane — chat */}
        <section className={`flex-1 flex flex-col min-w-0 ${selectedId ? "flex" : "hidden md:flex"}`}>
          {!selectedId && (
            <div className="flex-1 flex items-center justify-center" style={{ background: "#f0f2f5" }}>
              <div className="text-center max-w-md">
                <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "#dadde1" }}>
                  <ChatCircle size={56} weight="duotone" className="text-slate-500" />
                </div>
                <div className="text-lg font-light tracking-wide text-slate-700 font-['Chivo']">{t("convv2.pick_title")}</div>
                <div className="text-xs text-slate-500 mt-1">{t("convv2.pick_subtitle")}</div>
              </div>
            </div>
          )}
          {selectedId && conv && (
            <>
              {/* Conv header — avatar + title + subtitle */}
              <div className="shrink-0 px-3 md:px-4 py-2 flex items-center gap-3" style={{ background: "#f0f2f5", borderBottom: "1px solid #d1d7db" }}>
                <button onClick={() => setSearchParams({})} className="md:hidden w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-200 rounded-full">
                  <ArrowLeft size={18} weight="bold" />
                </button>
                <div
                  className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm"
                  style={{ background: avatarColor(conv.title || "?") }}
                >
                  {(conv.title || "?").split("›").pop().trim().charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold text-slate-900 truncate font-['Chivo']" data-testid="conv-title">{conv.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {conv._stub ? (
                      <span className="text-slate-400">{t("convv2.stub_loading", "loading…")}</span>
                    ) : (
                      <>
                        {conv.pcs.length === 1
                          ? t("convv2.companies_one", "{count} company", { count: conv.pcs.length })
                          : t("convv2.companies_many", "{count} companies", { count: conv.pcs.length })}
                        {" · "}{(conv.active_users || []).length} active
                      </>
                    )}
                    {wsConnected && !conv._stub && (
                      <span className="ml-2 inline-flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#00a884" }} />
                        <span style={{ color: "#00a884" }}>{t("convv2.live_pill", "live")}</span>
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowInvite(true)}
                  data-testid="conv-invite-btn"
                  title={t("convv2.btn_invite_user")}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500"
                >
                  <UserPlus size={15} weight="bold" />
                </button>
                <button
                  onClick={() => setShowAddCompany(true)}
                  data-testid="conv-add-company-btn"
                  title={t("convv2.btn_add_company")}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500"
                >
                  <Plus size={15} weight="bold" />
                </button>
                {/* Hide Join/Leave during the stub window — we don't yet
                    know who's active so flashing Join then Leave is jarring. */}
                {!conv._stub && (!isActiveInThisConv ? (
                  <button
                    onClick={onJoin}
                    data-testid="conv-join-btn"
                    className="h-8 px-3 rounded-full text-white text-[11px] font-bold uppercase tracking-wider hover:brightness-95 transition"
                    style={{ background: "#00a884" }}
                  >
                    Join
                  </button>
                ) : (
                  <button
                    onClick={onLeave}
                    data-testid="conv-leave-btn"
                    className="h-8 px-3 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-600 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1"
                    title={t("convv2.btn_leave_conv")}
                  >
                    <SignOutIcon size={11} weight="bold" /> Leave
                  </button>
                ))}
                {/* iter133 — overflow menu: Close conversation. Hidden
                    if the user lacks authorization or the conversation
                    is already closed. The DotsThreeVertical is the only
                    way to discover archive — keeps the header clean. */}
                {canClose && (
                  <div className="relative">
                    <button
                      onClick={() => setShowConvMenu((v) => !v)}
                      data-testid="conv-header-menu-btn"
                      className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500"
                      title={t("convv2.btn_more_actions")}
                    >
                      <DotsThreeVertical size={16} weight="bold" />
                    </button>
                    {showConvMenu && (
                      <>
                        {/* Backdrop captures click-outside to dismiss. */}
                        <div
                          className="fixed inset-0 z-30"
                          onClick={() => setShowConvMenu(false)}
                        />
                        <div
                          className="absolute right-0 top-10 z-40 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[180px]"
                          data-testid="conv-header-menu"
                        >
                          <button
                            onClick={onClose}
                            data-testid="conv-close-btn"
                            className="w-full text-left px-3 py-2 text-[12px] font-medium hover:bg-rose-50 flex items-center gap-2 text-rose-600"
                          >
                            <Trash size={12} weight="bold" /> Close conversation
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {conv.archived && (
                  <span
                    data-testid="conv-archived-badge"
                    className="ml-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-slate-500 border border-slate-300 bg-slate-100"
                  >
                    Closed
                  </span>
                )}
              </div>

              {/* iter135 — share-in-progress banner for non-Presenters */}
              <ShareBanner
                conversationId={selectedId}
                share={shareState.share}
                currentUserId={me?.user_id}
                currentUserType={me?.user_type}
                onJoined={shareState.onJoined}
              />

              {/* PCS chip row — placeholder during stub state */}
              <div className="shrink-0 px-3 md:px-4 py-1.5 flex items-center gap-1.5 overflow-x-auto bg-white border-b border-slate-100">
                <Info size={11} className="text-slate-400 shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 shrink-0">PCS</span>
                {conv._stub && (
                  <span
                    className="inline-block w-16 h-4 rounded-full bg-slate-100 animate-pulse"
                    data-testid="conv-pcs-skeleton"
                  />
                )}
                {(conv.pcs || []).map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono text-slate-700 shrink-0 border"
                    style={{ background: "#f0f2f5", borderColor: "#d1d7db" }}
                    data-testid={`conv-pcs-chip-${c}`}
                  >
                    {c}
                  </span>
                ))}
              </div>

              {/* Message list — beige polka-dot WhatsApp background.
                  iter135.15 — Phase C: the list is now virtualised
                  via ``react-virtuoso``. The outer wrapper provides
                  positioning for overlays (drag, jump-to-latest); the
                  inner ``MessageList`` owns its own scroller and
                  exposes ``scrollerRef`` + ``scrollToBottom`` to the
                  page. ``loadOlder`` is wired to ``onStartReached``;
                  ``showJumpToBottom`` is wired to
                  ``onAtBottomChange``. */}
              <div
                className="relative flex-1 flex flex-col min-h-0"
                onDragEnter={onPaneDragEnter}
                onDragOver={onPaneDragOver}
                onDragLeave={onPaneDragLeave}
                onDrop={onPaneDrop}
              >
                {/* iter134.11 — drag-over overlay. ``pointer-events-none``
                    so the overlay doesn't intercept the drop event. */}
                {isDragOver && (
                  <div
                    className="absolute inset-2 z-10 rounded-md border-2 border-dashed flex items-center justify-center pointer-events-none"
                    style={{
                      borderColor: "#00a884",
                      background: "rgba(255, 255, 255, 0.85)",
                      backdropFilter: "blur(2px)",
                    }}
                    data-testid="conv-drop-overlay"
                  >
                    <div className="text-center">
                      <Paperclip size={28} weight="duotone" className="mx-auto mb-2" style={{ color: "#00a884" }} />
                      <div className="text-[14px] font-bold uppercase tracking-wider" style={{ color: "#00a884" }}>
                        {t("convv2.drop_to_send")}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {t("convv2.drop_hint")}
                      </div>
                    </div>
                  </div>
                )}
                <MessageList
                  ref={messageListRef}
                  convId={selectedId}
                  currentUserKey={me ? `${me.user_type}:${me.user_id}` : null}
                  isStaff={me?.user_type === "staff"}
                  onEditMessage={onEditMessage}
                  onRecallMessage={onRecallMessage}
                  onStartReached={loadOlder}
                  onAtBottomChange={onAtBottomChange}
                  unreadAnchorSeq={unreadAnchorSeq}
                  scrollerRef={bindScroller}
                  // iter136.3 — followOutput=false. Letting Virtuoso
                  // own the auto-snap-on-append path runs through its
                  // ``scrollToIndex({align:"end"})`` which leaves the
                  // last bubble one row above the FooterSlot (typing
                  // indicator + bottom safe-area spacer) — visually
                  // "scrolled to N-1". Our own message-arrival
                  // useEffect handles snap-to-bottom via the imperative
                  // ``messageListRef.scrollToBottom`` which also nudges
                  // the raw scroller to ``scrollHeight`` so the footer
                  // doesn't push the latest bubble out of view.
                  followOutput={false}
                  className="flex-1 py-3"
                  style={{
                    background: "#efeae2",
                    backgroundImage: WA_PATTERN,
                    // iter135.19 — explicit ``overflow-x: hidden``
                    // belt-and-braces: even if a child (e.g. a very
                    // wide image attachment) exceeds the viewport
                    // width we'd rather clip than introduce a
                    // horizontal scrollbar on the whole pane.
                    overflowX: "hidden",
                  }}
                  data-testid="conv-messages"
                  header={() => (
                    <div className="px-4 sm:px-12">
                      {loadingMore && (
                        <div className="flex justify-center mb-2" data-testid="conv-history-loading">
                          <CircleNotch
                            size={16}
                            weight="bold"
                            className="animate-spin text-slate-400"
                            aria-label={t("convv2.loading_older")}
                          />
                        </div>
                      )}
                      {!hasMoreHistory && messages.length >= 20 && (
                        <div className="flex justify-center mb-2 text-[10px] uppercase tracking-wider text-slate-400" data-testid="conv-history-end">
                          {t("convv2.beginning")}
                        </div>
                      )}
                    </div>
                  )}
                  footer={() => (
                    <div className="px-4 sm:px-12">
                      <div ref={messagesEndRef} />
                      {typingUsers.length > 0 && (
                        <div data-testid="conv-typing-indicator" className="flex justify-start mt-1">
                          <div className="px-3 py-1.5 rounded-lg shadow-sm text-[11px] text-slate-500 italic flex items-center gap-1.5"
                               style={{ background: "#ffffff" }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "150ms" }} />
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "300ms" }} />
                            <span>
                              {typingUsers.length === 1 ? t("convv2.typing_one") : t("convv2.typing_many").replace("{count}", typingUsers.length)}
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Bottom safe-area — iter135.19. Virtuoso
                          clamps scroll to the bottom of its content
                          box; without this spacer the final bubble
                          sits flush against the composer border and
                          the timestamp/time-read receipt gets clipped
                          by the composer's top edge. */}
                      <div aria-hidden className="h-4" />
                    </div>
                  )}
                  emptyComponent={
                    loadingConv ? (
                      <div className="flex justify-center mt-6" data-testid="conv-messages-loading">
                        <CircleNotch
                          size={20}
                          weight="bold"
                          className="animate-spin text-slate-400"
                          aria-label={t("convv2.aria_loading_messages", "Loading messages")}
                        />
                      </div>
                    ) : (
                      <div className="flex justify-center mt-6">
                        <div className="text-[12px] text-slate-700 px-3 py-2 rounded-md shadow-sm" style={{ background: "#fff3c4" }}>
                          {t("convv2.empty_msgs")}
                        </div>
                      </div>
                    )
                  }
                />
              {/* iter135.7 — floating "jump to latest" arrow. Surfaces
                  whenever the user has scrolled up by more than ~240 px
                  from the bottom; clicking it smooth-scrolls to the
                  newest message via ``snapToBottom`` and clears the
                  scroll-interrupt flag so future incoming messages
                  resume auto-snap behaviour. */}
              {showJumpToBottom && (
                <button
                  type="button"
                  onClick={() => {
                    userInterruptedScrollRef.current = false;
                    setShowJumpToBottom(false);
                    snapToBottom("smooth");
                  }}
                  data-testid="conv-jump-to-latest"
                  title={t("convv2.jump_to_latest")}
                  className="absolute bottom-3 right-3 z-20 w-10 h-10 flex items-center justify-center rounded-full shadow-md transition-all hover:shadow-lg hover:scale-105"
                  style={{
                    background: "#ffffff",
                    color: "#54656f",
                    border: "1px solid #d1d7db",
                  }}
                >
                  <ArrowDown size={18} weight="bold" />
                </button>
              )}
              </div>

              {/* Composer — hidden for archived (closed) conversations.
                  Sends would 410 server-side anyway; the read-only
                  banner makes the state explicit to the user. */}
              {conv.archived ? (
                <div
                  className="shrink-0 px-3 py-3 text-center"
                  style={{ background: "#f0f2f5", borderTop: "1px solid #d1d7db" }}
                  data-testid="conv-archived-banner"
                >
                  <div className="text-[12px] text-slate-600 font-medium">
                    {t("convv2.archived_title")}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {t("convv2.archived_subtitle")}
                  </div>
                </div>
              ) : (
              <div className="shrink-0 px-3 py-2.5" style={{ background: "#f0f2f5", borderTop: "1px solid #d1d7db" }}>
                <div className="flex items-end gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUploadAndSend(f);
                      e.target.value = "";
                    }}
                    className="hidden"
                    data-testid="conv-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="conv-attach-btn"
                    title={t("convv2.btn_attach")}
                    className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500"
                  >
                    <Paperclip size={18} weight="bold" />
                  </button>
                  {/* iter135 — Screen Share button (b: composer toolbar) */}
                  <ShareButton
                    conversationId={selectedId}
                    archived={conv?.archived}
                    onShareStart={shareState.onShareStart}
                  />
                  <textarea
                    ref={composerRef}
                    value={composerValue}
                    onChange={onComposerChange}
                    onKeyDown={(e) => {
                      // iter136 — when the mention picker is open,
                      // it owns ↑↓/Enter/Esc via its own window-level
                      // keydown listener (capture phase). We only
                      // need to keep Enter from firing onSend below.
                      if (mentionState && (
                        e.key === "Enter" || e.key === "Tab" ||
                        e.key === "ArrowUp" || e.key === "ArrowDown" ||
                        e.key === "Escape"
                      )) {
                        // Picker handler stops propagation already; this
                        // is belt-and-suspenders so we don't accidentally
                        // submit the message on Enter while picking.
                        if (e.key === "Enter" || e.key === "Tab") {
                          e.preventDefault();
                        }
                        return;
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        onSend();
                      }
                    }}
                    onKeyUp={(e) => {
                      // Re-evaluate the mention picker after arrow-key
                      // navigation that doesn't trigger onChange (caret
                      // moves but value doesn't).
                      if (e.key === "ArrowLeft" || e.key === "ArrowRight" ||
                          e.key === "Home" || e.key === "End") {
                        setMentionState(
                          computeMentionState(
                            e.target.value,
                            e.target.selectionStart,
                            e.target,
                          ),
                        );
                      }
                    }}
                    onPaste={onComposerPaste}
                    placeholder={t("convv2.composer_placeholder")}
                    rows={1}
                    data-testid="conv-composer"
                    className="flex-1 resize-none rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none px-3 py-2 text-sm bg-white"
                    style={{ maxHeight: 120 }}
                  />
                  <button
                    onClick={onSend}
                    disabled={!composerValue.trim()}
                    data-testid="conv-send-btn"
                    className="h-10 w-10 flex items-center justify-center rounded-full text-white disabled:opacity-40 hover:brightness-95 transition"
                    style={{ background: "#00a884" }}
                  >
                    <PaperPlaneTilt size={16} weight="fill" />
                  </button>
                </div>
                <div className="text-[10px] text-slate-400 mt-1.5 px-1">
                  {t("convv2.composer_hint")}
                </div>
                {mentionState && selectedId && (
                  <MentionPicker
                    conversationId={selectedId}
                    query={mentionState.query}
                    anchor={mentionState.anchor}
                    onSelect={onMentionSelect}
                    onClose={() => setMentionState(null)}
                  />
                )}
              </div>
              )}
            </>
          )}
        </section>
      </div>

      {showInitiate && <InitiateModal onClose={() => setShowInitiate(false)} onInitiate={onInitiate} onRequest={onRequestInitiate} />}
      {showAddCompany && <AddCompanyModal conversationId={selectedId} onClose={() => setShowAddCompany(false)} onAdd={onAddCompany} onRequest={onRequestCompany} />}
      {showInvite && (
        <InviteModal
          conversationId={selectedId}
          onClose={() => setShowInvite(false)}
        />
      )}
      {showInbox && (
        <InboxModal
          onClose={() => { setShowInbox(false); reloadInboxCount(); reloadList(); }}
          onJump={(cid) => {
            setShowInbox(false);
            setSearchParams({ c: cid });
            reloadInboxCount();
            reloadList();
          }}
        />
      )}
      <NotifPrefsPanel open={showNotifPrefs} onClose={() => setShowNotifPrefs(false)} />
      {/* iter135.13 — Screen-share Viewer overlay now lives in the
          global ``ViewerProvider`` (3 modes: fullpage / compact /
          fullscreen) so it survives route navigation. The Presenter
          modal also lives at the App level. */}
    </div>
  );
}


// ── Initiate modal ──────────────────────────────────────────────
// MessageBubble + formatBubbleTime moved to
// /app/frontend/src/pages/ConversationsV2/MessageBubble.jsx
// (iter135.11 — Phase A extraction).
// formatBubbleTime moved to MessageBubble.jsx
// bumpListPreview replaced by convStore.bumpConvPreview()
// avatarColor + formatListTime moved to SidebarRow.jsx



// ── Inbox modal — incoming + outgoing Apply-Up requests ──────────
function InboxModal({ onClose, onJump }) {
  const t = useT();
  const [tab, setTab] = useState("incoming");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    api.get(`/conv/requests?direction=${tab}&status=pending&limit=50`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { reload(); }, [reload]);

  const respond = async (rid, accept) => {
    setBusyId(rid);
    try {
      const r = await api.post(`/conv/requests/${rid}/respond`, { accept });
      toast.success(accept ? "Accepted — Company added" : "Declined");
      // For incoming-accept, jump to the conversation if we have it.
      if (accept && r.data?.conversation_id && onJump) onJump(r.data.conversation_id);
      else reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Respond failed");
    } finally { setBusyId(""); }
  };

  return (
    <Modal title={t("convv2.modal_inbox_title")} onClose={onClose} testId="conv-inbox-modal">
      <div className="flex border-b border-slate-200 mb-3 -mx-5 px-5">
        <button
          onClick={() => setTab("incoming")}
          data-testid="conv-inbox-tab-incoming"
          className={`flex-1 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
            tab === "incoming" ? "text-blue-700 border-b-2 border-blue-700 -mb-px" : "text-slate-500"
          }`}
        >
          <ArrowDown size={12} weight="bold" />
          Incoming
        </button>
        <button
          onClick={() => setTab("outgoing")}
          data-testid="conv-inbox-tab-outgoing"
          className={`flex-1 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
            tab === "outgoing" ? "text-blue-700 border-b-2 border-blue-700 -mb-px" : "text-slate-500"
          }`}
        >
          <ArrowUp size={12} weight="bold" />
          Outgoing
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto -mx-5 px-5">
        {loading && <div className="text-xs text-slate-400 py-6 text-center">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-slate-400 py-6 text-center">{t("convv2.inbox_empty_pending", "No pending {tab} requests.", { tab })}</div>
        )}
        {items.map((r) => (
          <div
            key={r.request_id}
            data-testid={`conv-inbox-row-${r.request_id}`}
            className="border border-slate-200 rounded-sm p-2.5 mb-2"
          >
            <div className="text-xs font-bold text-slate-900 truncate">
              {r.conversation_title || "(untitled)"}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {tab === "incoming" ? (
                <>
                  <strong>{r.requester_company_name || r.requester?.customer_id}</strong>{" "}
                  asks <strong>{r.target_company_name || r.target_customer_id}</strong> to join
                </>
              ) : (
                <>
                  Asked <strong>{r.target_company_name || r.target_customer_id}</strong> to join
                </>
              )}
            </div>
            <div className="text-[10px] font-mono text-slate-400 mt-1">
              expires {r.expires_at ? parseServerTime(r.expires_at).toLocaleString("en-US") : "—"}
            </div>
            {tab === "incoming" && (
              <div className="flex gap-2 mt-2">
                <button
                  data-testid={`conv-inbox-accept-${r.request_id}`}
                  disabled={busyId === r.request_id}
                  onClick={() => respond(r.request_id, true)}
                  className="flex-1 h-8 px-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-40"
                >
                  <Check size={12} weight="bold" /> Accept
                </button>
                <button
                  data-testid={`conv-inbox-decline-${r.request_id}`}
                  disabled={busyId === r.request_id}
                  onClick={() => respond(r.request_id, false)}
                  className="flex-1 h-8 px-2 rounded-sm border border-slate-300 hover:bg-slate-100 text-slate-600 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-40"
                >
                  <XIcon size={12} weight="bold" /> Decline
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}


// ── Invite modal — magic-link + OTP for external users (iter124) ──
function InviteModal({ conversationId, onClose }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sending, setSending] = useState("");
  const [result, setResult] = useState(null); // {email, expires_at, delivered, message}

  useEffect(() => {
    if (!conversationId) return;
    setLoading(true);
    api.get(`/conv/conversations/${conversationId}/invite/candidates`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [conversationId]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) => (it.name || "").toLowerCase().includes(q) ||
              (it.email || "").toLowerCase().includes(q) ||
              (it.company_name || "").toLowerCase().includes(q),
    );
  }, [items, filter]);

  const sendInvite = async (it) => {
    setSending(`${it.user_type}:${it.user_id}`);
    setResult(null);
    try {
      const r = await api.post(
        `/conv/conversations/${conversationId}/invite`,
        { target_user_type: it.user_type, target_user_id: it.user_id },
      );
      setResult({
        email: r.data.delivery_email,
        expires_at: r.data.expires_at,
        delivered: !!r.data.delivered,
        message: r.data.delivery_message || "",
      });
      if (r.data.delivered) toast.success(`Invite sent to ${r.data.delivery_email}`);
      else toast.info(`Invite minted (delivery: ${r.data.delivery_message || "n/a"})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setSending(""); }
  };

  return (
    <Modal title={t("convv2.modal_invite_title")} onClose={onClose} testId="conv-invite-modal">
      <p className="text-xs text-slate-500 mb-2">
        {t("convv2.invite_modal_hint", "Pick a person attached to a Company in this Conversation's PCS. They'll receive an emailed magic-link + one-time code to join.")}
      </p>
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={t("convv2.invite_filter_placeholder")}
        data-testid="conv-invite-filter"
        className="w-full px-3 py-2 text-sm rounded-sm border border-slate-300 focus:border-blue-500 focus:outline-none mb-2"
        autoFocus
      />
      <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-sm">
        {loading && <div className="p-3 text-xs text-slate-400 text-center">{t("convv2.invite_loading_candidates", "Loading candidates…")}</div>}
        {!loading && visible.length === 0 && (
          <div className="p-3 text-xs text-slate-400 text-center">
            {items.length === 0
              ? t("convv2.invite_no_candidates", "No emailable users in this PCS.")
              : t("convv2.invite_no_matches", "No matches.")}
          </div>
        )}
        {visible.map((it) => {
          const k = `${it.user_type}:${it.user_id}`;
          return (
            <button
              key={k}
              onClick={() => sendInvite(it)}
              disabled={sending === k}
              className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-blue-50 text-xs disabled:opacity-50"
              data-testid={`conv-invite-target-${it.user_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 truncate">
                    {it.name || `Person #${it.user_id}`}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                    <EnvelopeSimple size={10} weight="bold" /> {it.email}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400 truncate">
                    {it.company_name || it.customer_id}
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 shrink-0">
                  {sending === k ? t("convv2.action_invite_sending") : t("convv2.action_invite")}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {result && (
        <div
          className={`mt-3 text-xs rounded-sm px-3 py-2 border ${
            result.delivered
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
          data-testid="conv-invite-result"
        >
          {result.delivered
            ? <>{t("convv2.invite_sent_to", "Email sent to")} <strong>{result.email}</strong>. {t("convv2.invite_link_expires", "Link expires")} {parseServerTime(result.expires_at).toLocaleString("en-US")}.</>
            : <>{t("convv2.invite_minted_undelivered", "Invite minted but email could not be delivered:")} <em>{result.message}</em>. {t("convv2.invite_link_still_valid", "Link still valid until")} {parseServerTime(result.expires_at).toLocaleString("en-US")}.</>}
        </div>
      )}
    </Modal>
  );
}




// ── Visual helpers — WhatsApp-Web parity ───────────────────────────
// avatarColor + AVATAR_PALETTE + formatListTime moved to SidebarRow.jsx
// (iter135.11 — Phase A extraction).

// iter134.6 — Defensive timestamp parser. The backend currently emits
// ISO strings WITHOUT a timezone suffix (e.g. "2026-05-01T12:18:41")
// even though the underlying datetime is UTC. Without an explicit
// "Z" / "+00:00" suffix, ``new Date(str)`` interprets the value as
// LOCAL time per the JS spec, so a UTC noon timestamp displayed in
// Tokyo would not get +9h applied. We append "Z" when no timezone
// is present so the browser's locale conversion does the right
// thing and every chat surface (sidebar, bubble, edited badge,
// audit pane) shows times in the viewer's local zone.
//
// If a future backend change adds proper timezone suffixes, this
// helper degrades cleanly — values that already have a "+", "-",
// or "Z" suffix are returned untouched.
export function parseServerTime(s) {
  if (!s) return null;
  if (typeof s !== "string") return new Date(s);
  // ISO date-only ("YYYY-MM-DD") — keep as-is.
  if (s.length <= 10) return new Date(s);
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTz ? s : s + "Z");
}

// WhatsApp-Web ribbon polka-dot pattern (lightweight inline SVG).
const WA_PATTERN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='110' height='110' viewBox='0 0 110 110'><g fill='%23d1c7b9' fill-opacity='0.35'><path d='M0 0h2v2H0zM10 8h2v2h-2zM22 18h2v2h-2zM34 6h2v2h-2zM48 22h2v2h-2zM62 4h2v2h-2zM78 16h2v2h-2zM92 8h2v2h-2zM6 32h2v2H6zM18 44h2v2h-2zM32 38h2v2h-2zM44 50h2v2h-2zM58 36h2v2h-2zM72 48h2v2h-2zM86 40h2v2h-2zM100 30h2v2h-2zM2 64h2v2H2zM14 76h2v2h-2zM26 68h2v2h-2zM40 80h2v2h-2zM54 70h2v2h-2zM68 84h2v2h-2zM82 72h2v2h-2zM96 86h2v2h-2zM10 96h2v2h-2zM24 102h2v2h-2zM38 92h2v2h-2zM52 104h2v2h-2zM66 94h2v2h-2zM80 100h2v2h-2zM94 102h2v2h-2z'/></g></svg>\")";
